import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
const BlogManagement = () => {
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [galleryFiles, setGalleryFiles] = useState([]);
    const [galleryPreviews, setGalleryPreviews] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        excerpt: '',
        content: '',
        image: '',
        slug: '',
        published: false,
        categoryId: '',
    });
    const { toast } = useToast();
    // Generate slug from title
    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '') // Remove special characters
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
            .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
    };
    // Auto-generate slug when title changes
    useEffect(() => {
        if (formData.title && !editingPost) {
            setFormData(prev => ({
                ...prev,
                slug: generateSlug(prev.title)
            }));
        }
    }, [formData.title, editingPost]);
    useEffect(() => {
        fetchPosts();
        fetchCategories();
    }, []);
    const fetchCategories = async () => {
        try {
            const response = await fetch(`${API_URL}/categories?type=blog`);
            const data = await response.json();
            setCategories(data);
        }
        catch (error) {
            console.error('Failed to fetch categories');
        }
    };
    const fetchPosts = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${API_URL}/blog/all`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json();
            setPosts(data);
        }
        catch (error) {
            toast({ title: 'Error', description: 'Failed to fetch blog posts', variant: 'destructive' });
        }
    };
    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };
    const handleGalleryChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            setGalleryFiles(prev => [...prev, ...files]);
            files.forEach(file => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setGalleryPreviews(prev => [...prev, reader.result]);
                };
                reader.readAsDataURL(file);
            });
        }
    };
    const removeGalleryImage = (index) => {
        setGalleryFiles(prev => prev.filter((_, i) => i !== index));
        setGalleryPreviews(prev => prev.filter((_, i) => i !== index));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('title', formData.title);
            formDataToSend.append('excerpt', formData.excerpt);
            formDataToSend.append('content', formData.content);
            formDataToSend.append('slug', formData.slug);
            formDataToSend.append('published', formData.published.toString());
            if (formData.categoryId) {
                formDataToSend.append('categoryId', formData.categoryId);
            }
            if (imageFile) {
                formDataToSend.append('image', imageFile);
            }
            // Gallery images (multiple, separate field)
            galleryFiles.forEach((file) => {
                formDataToSend.append('gallery', file);
            });
            const url = editingPost ? `${API_URL}/blog/${editingPost.id}` : `${API_URL}/blog`;
            const method = editingPost ? 'PUT' : 'POST';
            const response = await fetch(url, {
                method,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formDataToSend,
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                console.error('Save failed:', response.status, errorData);
                throw new Error(errorData.error || `Server returned ${response.status}`);
            }
            toast({ title: 'Success', description: `Blog post ${editingPost ? 'updated' : 'created'} successfully` });
            setIsOpen(false);
            resetForm();
            fetchPosts();
        }
        catch (error) {
            console.error('Blog save error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to save blog post';
            toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
        }
    };
    const handleDelete = async (id) => {
        if (!confirm('Are you sure you want to delete this blog post?'))
            return;
        const token = localStorage.getItem('token');
        try {
            await fetch(`${API_URL}/blog/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            toast({ title: 'Success', description: 'Blog post deleted successfully' });
            fetchPosts();
        }
        catch (error) {
            toast({ title: 'Error', description: 'Failed to delete blog post', variant: 'destructive' });
        }
    };
    const handleEdit = (post) => {
        setEditingPost(post);
        setFormData({
            title: post.title,
            excerpt: post.excerpt,
            content: post.content,
            image: post.image,
            slug: post.slug,
            published: post.published,
            categoryId: post.categoryId || '',
        });
        // Handle both external URLs and local paths for image preview
        const previewUrl = post.image
            ? (post.image.startsWith('http') ? post.image : `https://qbcron.com${post.image}`)
            : '';
        setImagePreview(previewUrl);
        setIsOpen(true);
    };
    const resetForm = () => {
        setEditingPost(null);
        setFormData({ title: '', excerpt: '', content: '', image: '', slug: '', published: false, categoryId: '' });
        setImageFile(null);
        setImagePreview('');
        setGalleryFiles([]);
        setGalleryPreviews([]);
    };
    return (_jsxs("div", { children: [_jsxs("div", { className: "flex justify-between items-center mb-8", children: [_jsx("h1", { className: "text-3xl font-bold", children: "Blog Management" }), _jsxs(Dialog, { open: isOpen, onOpenChange: (open) => { setIsOpen(open); if (!open)
                            resetForm(); }, children: [_jsx(DialogTrigger, { asChild: true, children: _jsxs(Button, { children: [_jsx(Plus, { className: "mr-2 h-4 w-4" }), "Add Blog Post"] }) }), _jsxs(DialogContent, { className: "max-w-3xl max-h-[90vh] overflow-y-auto", children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: editingPost ? 'Edit Blog Post' : 'Add New Blog Post' }), _jsx(DialogDescription, { children: "Create engaging content for your audience" })] }), _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { children: [_jsx(Label, { htmlFor: "title", children: "Title" }), _jsx(Input, { id: "title", value: formData.title, onChange: (e) => setFormData({ ...formData, title: e.target.value }), required: true })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "slug", children: "Slug" }), _jsx(Input, { id: "slug", value: formData.slug, onChange: (e) => setFormData({ ...formData, slug: e.target.value }), required: true })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "category", children: "Category" }), _jsxs(Select, { value: formData.categoryId || undefined, onValueChange: (value) => setFormData({ ...formData, categoryId: value === 'none' ? '' : value }), children: [_jsx(SelectTrigger, { id: "category", children: _jsx(SelectValue, { placeholder: "Select category (optional)" }) }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: "none", children: "No Category" }), categories.map((category) => (_jsx(SelectItem, { value: category.id, children: category.name }, category.id)))] })] })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "excerpt", children: "Excerpt" }), _jsx(Textarea, { id: "excerpt", value: formData.excerpt, onChange: (e) => setFormData({ ...formData, excerpt: e.target.value }), required: true })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "content", children: "Content" }), _jsx(ReactQuill, { theme: "snow", value: formData.content, onChange: (value) => setFormData({ ...formData, content: value }), modules: {
                                                            toolbar: [
                                                                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                                                ['bold', 'italic', 'underline', 'strike'],
                                                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                                [{ 'color': [] }, { 'background': [] }],
                                                                ['link', 'image', 'video'],
                                                                ['clean']
                                                            ]
                                                        }, className: "bg-background", style: { height: '300px', marginBottom: '50px' } })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "image", children: "Featured Image" }), _jsx(Input, { id: "image", type: "file", accept: "image/*", onChange: handleImageChange, className: "cursor-pointer" }), imagePreview && (_jsx("div", { className: "mt-2", children: _jsx("img", { src: imagePreview, alt: "Preview", className: "max-h-40 rounded border" }) }))] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "gallery", children: "Gallery Images (Optional)" }), _jsx(Input, { id: "gallery", type: "file", accept: "image/*", multiple: true, onChange: handleGalleryChange, className: "cursor-pointer" }), galleryPreviews.length > 0 && (_jsx("div", { className: "mt-2 grid grid-cols-4 gap-2", children: galleryPreviews.map((preview, index) => (_jsxs("div", { className: "relative group", children: [_jsx("img", { src: preview, alt: `Gallery ${index + 1}`, className: "h-20 w-full object-cover rounded border" }), _jsx("button", { type: "button", onClick: () => removeGalleryImage(index), className: "absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity", children: _jsx(Trash2, { className: "h-3 w-3" }) })] }, index))) }))] }), _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(Switch, { id: "published", checked: formData.published, onCheckedChange: (checked) => setFormData({ ...formData, published: checked }) }), _jsx(Label, { htmlFor: "published", children: "Published" })] }), _jsxs(Button, { type: "submit", className: "w-full", children: [editingPost ? 'Update' : 'Create', " Post"] })] })] })] })] }), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6", children: posts.map((post) => (_jsxs(Card, { className: "flex flex-col", children: [_jsx(CardHeader, { children: _jsx(CardTitle, { className: "text-xl line-clamp-2", children: post.title }) }), _jsxs(CardContent, { className: "flex-1", children: [post.image && (_jsx("img", { src: post.image.startsWith('http') ? post.image : `https://qbcron.com${post.image}`, alt: post.title, className: "w-full h-48 object-cover rounded mb-3" })), _jsx("p", { className: "text-muted-foreground mb-3 line-clamp-2 text-sm", children: post.excerpt }), _jsxs("div", { className: "space-y-1 text-xs text-muted-foreground mb-4", children: [_jsxs("div", { children: ["Slug: ", post.slug] }), _jsxs("div", { children: ["Status: ", _jsx("span", { className: post.published ? 'text-green-500' : 'text-yellow-500', children: post.published ? 'Published' : 'Draft' })] })] }), _jsxs("div", { className: "flex gap-2 mt-auto", children: [_jsxs(Button, { size: "sm", variant: "outline", onClick: (e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleEdit(post);
                                            }, className: "flex-1", type: "button", children: [_jsx(Pencil, { className: "h-4 w-4 mr-1" }), " Edit"] }), _jsx(Button, { size: "sm", variant: "destructive", onClick: (e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleDelete(post.id);
                                            }, type: "button", children: _jsx(Trash2, { className: "h-4 w-4" }) })] })] })] }, post.id))) })] }));
};
export default BlogManagement;
